import type { LockedFeatureKey } from '../../constants/bloqueios';
import type { Artist } from '../../interfaces/maestra';
import { perfilEstaAtivo } from '../../hooks/useArtistCapabilities';
import { PAGINAS_DO_ACESSO, acessoAPagina, acessoAoModulo, type ModuloDoPerfil, type PaginaDoAcesso } from '../../nucleo/permissoesDoMembro';

// QUEM ENTRA EM CADA MÓDULO (a web e o app leem daqui). Perfil ativo entra em todos. No perfil de um titular sem assinatura,
// o convidado ENTRA e LÊ os módulos que têm o que ler (fica sem caneta, não sem acesso); os que
// criam ou gastam (wizard, refazer, Nyta) ficam de fora. O dono sem assinatura vê o convite.
// É a mesma regra do `RequireArtistActive`, num lugar só para o Início poder perguntar antes.

export const LEITURA_PARA_MEMBRO = new Set<LockedFeatureKey>(['catalog', 'agenda', 'plano', 'team', 'arquivos', 'cronograma']);

/**
 * O que o código de acesso libera para o dono (decisão do Lucas, 27/09/2026): o planejamento (o
 * wizard) e o plano de ação que nasce dele. Os módulos seguem pedindo o PRO.
 */
export const LIBERADO_PELO_CODIGO = new Set<LockedFeatureKey>(['planning', 'plano', 'tasks']);

/** A regra em si, pelo que ela decide: o perfil está ativo? quem olha é convidado? */
export const entraComAcesso = (perfilAtivo: boolean, ehMembro: boolean, feature: LockedFeatureKey): boolean => (
  perfilAtivo || (ehMembro && LEITURA_PARA_MEMBRO.has(feature))
);

export const entraNoModulo = (artist: Artist, isPro: boolean, feature: LockedFeatureKey): boolean => (
  // O convidado sem acesso ao módulo não entra, ativo ou não (o `MODULO_DO_BLOQUEIO`, abaixo).
  !membroSemAcesso(artist, MODULO_DO_BLOQUEIO[feature])
  && (
    entraComAcesso(perfilEstaAtivo(artist, isPro), artist.role === 'member', feature)
    || (artist.role !== 'member' && !!artist.liberado_por_codigo && LIBERADO_PELO_CODIGO.has(feature))
  )
);

/** O que o acesso do convidado guarda: uma página (03/11/2026) ou uma área inteira (o módulo). */
export type AreaDoAcesso = ModuloDoPerfil | PaginaDoAcesso;

/**
 * A página (ou a área) do acesso por convite (`nucleo/permissoesDoMembro`) que cada bloqueio guarda.
 * A Equipe e a Nyta não entram: a Equipe é do titular, e a Nyta segue quem paga.
 */
export const MODULO_DO_BLOQUEIO: Partial<Record<LockedFeatureKey, AreaDoAcesso>> = {
  planning: 'plan',
  plano: 'plan',
  tasks: 'plan',
  refazer: 'plan',
  negociacoes: 'vendas',
  // O Perfil tem a página dele desde 03/11/2026 (antes, ia com Vendas), como a RLS de `perfis_publicos`.
  perfil: 'perfil',
  shows: 'producao',
  cronograma: 'cronograma',
  // Músicas: Projetos ou Playlists (quem monta playlists lê as músicas).
  catalog: 'catalog',
  agenda: 'agenda',
  arquivos: 'arquivos',
  // O grupo do Marketing: qualquer página dele. Branding e Material pedem a sua na rota.
  marketing: 'marketing',
};

const PAGINAS = new Set<string>(PAGINAS_DO_ACESSO);
const acessoNaArea = (niveis: readonly string[] | null | undefined, area: AreaDoAcesso) => (
  PAGINAS.has(area) ? acessoAPagina(niveis, area as PaginaDoAcesso) : acessoAoModulo(niveis, area as ModuloDoPerfil)
);

/**
 * O convidado está SEM ACESSO a este módulo? O titular não o liberou nem para Ver. O menu continua
 * igual; quem pergunta isto mostra o aviso no lugar do conteúdo (decisão do Lucas, 03/10/2026).
 */
export const membroSemAcesso = (artist: Artist, area: AreaDoAcesso | undefined): boolean => (
  !!area && artist.role === 'member' && acessoNaArea(artist.access_levels, area) === 'nenhum'
);
